import { useState } from 'react'
import { STATIC_PAGE_HEADERS } from "~/lib/cache.server"
import type { MetaFunction } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema, StructuredData } from '~/components/structured-data'
import { db } from '@afficheservices/db'
import { serviceCategories } from '@afficheservices/db/schema'
import { isNull, sql } from 'drizzle-orm'
import { ArrowRight, Search } from 'lucide-react'
import { iconForService } from '~/lib/service-icons'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Tous les services — AfficheServices' },
  { name: 'description', content: 'Trouvez un artisan pour tous vos travaux : plomberie, electricite, peinture, renovation et plus de 200 categories.' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/services' },
  { property: 'og:image', content: 'https://www.afficheservices.fr/api/og?title=Tous+nos+services&subtitle=20%2B+categories' },
]

const COLOR_MAP: Record<string, string> = {
  plomberie: 'bg-blue-50 text-blue-600', electricite: 'bg-amber-50 text-amber-600',
  peinture: 'bg-pink-50 text-pink-600', renovation: 'bg-orange-50 text-orange-600',
  menuiserie: 'bg-emerald-50 text-emerald-600', toiture: 'bg-red-50 text-red-600',
  serrurerie: 'bg-violet-50 text-violet-600', nettoyage: 'bg-cyan-50 text-cyan-600',
  jardinage: 'bg-green-50 text-green-600', climatisation: 'bg-sky-50 text-sky-600',
  demenagement: 'bg-indigo-50 text-indigo-600', 'carrelage-sol': 'bg-stone-100 text-stone-600',
}

export async function loader() {
  const parents = await db
    .select({ id: serviceCategories.id, name: serviceCategories.nameFr, slug: serviceCategories.slug, leadPriceMin: serviceCategories.leadPriceMin, leadPriceMax: serviceCategories.leadPriceMax })
    .from(serviceCategories)
    .where(isNull(serviceCategories.parentId))
    .orderBy(serviceCategories.displayOrder)

  const children = await db
    .select({ id: serviceCategories.id, name: serviceCategories.nameFr, slug: serviceCategories.slug, parentId: serviceCategories.parentId })
    .from(serviceCategories)
    .where(sql`${serviceCategories.parentId} IS NOT NULL`)

  return {
    categories: parents.map((p) => ({
      ...p,
      children: children.filter((c) => c.parentId === p.id),
    })),
  }
}

export default function ServicesIndex() {
  const { categories } = useLoaderData<typeof loader>()
  const [search, setSearch] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const filtered = search
    ? categories.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()) || c.children.some((ch) => ch.name.toLowerCase().includes(search.toLowerCase())))
    : categories

  // Build flat suggestion list from all categories + children
  const allServices = categories.flatMap((c) => [
    { name: c.name, slug: c.slug, type: 'parent' as const },
    ...c.children.map((ch) => ({ name: ch.name, slug: c.slug, type: 'child' as const })),
  ])
  const suggestions = search.length >= 2
    ? allServices.filter(s => s.name.toLowerCase().includes(search.toLowerCase())).slice(0, 6)
    : []

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Services', url: 'https://www.afficheservices.fr/services' },
      ]} />
      {/* ItemList schema so Google understands /services as a catalog hub
          (audit #20). Each top-level category links out to its own page. */}
      <StructuredData data={{
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Tous les services AfficheServices',
        numberOfItems: categories.length,
        itemListElement: categories.map((cat, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: cat.name,
          url: `https://www.afficheservices.fr/services/${cat.slug}`,
        })),
      }} />
      <div className="mx-auto max-w-7xl px-4 py-12 lg:px-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900">Tous nos services</h1>
          <p className="mt-3 text-slate-500">
            {categories.length} catégories · {categories.reduce((s, c) => s + c.children.length, 0)}+ sous-catégories
          </p>
          <div className="relative mx-auto mt-6 max-w-md">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher un service..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setShowSuggestions(true) }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className="w-full rounded-xl border border-slate-200 py-3 pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            {/* Autocomplete dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute left-0 right-0 z-20 mt-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
                {suggestions.map((s, i) => (
                  <Link
                    key={`${s.slug}-${s.name}-${i}`}
                    to={`/services/${s.slug}`}
                    className="flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-primary-50 transition-colors"
                    onClick={() => { setSearch(''); setShowSuggestions(false) }}
                  >
                    <span className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold ${
                      s.type === 'parent' ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-400'
                    }`}>
                      {s.type === 'parent' ? 'CAT' : 'SOUS'}
                    </span>
                    <span className="text-slate-900">{s.name}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((cat) => {
            const Icon = iconForService(cat.slug)
            const color = COLOR_MAP[cat.slug] ?? 'bg-slate-50 text-slate-600'
            return (
              <Link key={cat.id} to={`/services/${cat.slug}`} className="card-hover group rounded-xl border border-border bg-white p-6 shadow-xs">
                <div className="flex items-center gap-3">
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${color}`}><Icon className="h-5 w-5" /></div>
                  <div>
                    <h2 className="text-base font-semibold text-slate-900 group-hover:text-primary">{cat.name}</h2>
                    <p className="text-xs text-slate-400">{cat.children.length} sous-catégories</p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {cat.children.slice(0, 5).map((ch) => (
                    <span key={ch.id} className="rounded-md bg-slate-50 px-2 py-1 text-xs text-slate-500 transition-colors group-hover:bg-primary/5 group-hover:text-primary">{ch.name}</span>
                  ))}
                  {cat.children.length > 5 && (
                    <span className="rounded-md border border-dashed border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-500 transition-colors group-hover:border-primary group-hover:text-primary">
                      + {cat.children.length - 5} autre{cat.children.length - 5 > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </Link>
            )
          })}
        </div>
        <div className="mt-10 text-center">
          <Link to="/demande-devis" className="inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3.5 text-sm font-semibold text-white shadow-md hover:bg-primary-hover btn-press">
            Demander un devis gratuit <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
      {/* Market overview */}
      <section className="border-t border-slate-100 bg-white py-14">
        <div className="mx-auto max-w-3xl px-4 lg:px-6">
          <h2 className="text-xl font-bold text-slate-900">Le marché des services à domicile en France</h2>
          <div className="mt-6 space-y-3 text-sm leading-relaxed text-slate-600">
            <p>Le secteur du bâtiment et des services à domicile en France représente <strong>plus de 150 milliards d'euros de chiffre d'affaires</strong> et emploie 1,5 million de salariés répartis dans 620 000 entreprises artisanales (source : FFB 2026).</p>
            <p>Les métiers les plus demandés sont la <strong>plomberie</strong> (25 % des demandes), l'<strong>électricité</strong> (18 %), la <strong>peinture</strong> (15 %), la <strong>rénovation générale</strong> (12 %) et la <strong>menuiserie</strong> (8 %). La climatisation et l'isolation connaissent la plus forte croissance (+25 % par an) portée par la transition énergétique.</p>
            <p>En moyenne, un ménage français dépense <strong>2 500 à 4 000 € par an</strong> en travaux et entretien de son logement. Les propriétaires de maisons individuelles dépensent 40 % de plus que les copropriétaires. L'Île-de-France concentre 22 % de la dépense nationale.</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
